import { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./App.css";
import "./AudioBlobAnimations.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { useFcmPush } from "./hooks/useFcmPush";
import { AuthCallback } from "./components/AuthCallback";
import { Header } from "./components/Header";
import { Login } from "./pages/Login";
import Register from "./pages/Register";
import { Profile } from "./pages/Profile";
import { CreateRide } from './pages/CreateRide';
import { RideSearch } from './pages/RideSearch';
import MyRides from './pages/MyRides'; 
import { BookingSuccess } from './pages/BookingSuccess';
import { ProfileSetup } from './pages/ProfileSetup';
import { PublicProfile } from './pages/PublicProfile';
import { PhoneVerification } from './pages/PhoneVerification';
import { ForgotPassword, ResetPassword } from './pages/PasswordReset';
import { AdminDashboard } from './pages/AdminDashboard';
import { NotificationsPanel } from './components/NotificationsPanel';
import Navbar from './components/Navbar';
import { Footer } from './components/Footer';
import { ProtectedRoute } from './components/ProtectedRoute';
import { VoiceMatch } from './components/VoiceMatch';
import { Impressum } from "./pages/Impressum";
import { AGB } from "./pages/AGB";
import { FAQ } from "./pages/FAQ";
import PublicPricingPage from "./pages/PublicPricingPage";
import TestMapPage from './pages/TestMapPage';
import DriverOnline from './pages/DriverOnline';
import DriverEarnings from './pages/DriverEarnings';
import SafetyDashboard from './pages/SafetyDashboard';
import OrbPage from './pages/OrbPage';
import OrbAppPage from './pages/OrbAppPage';
import { OrbProtectedRoute } from './components/OrbProtectedRoute';
import B2BDailyRoute from './pages/B2BDailyRoute';
import OnboardingWizard from './pages/OnboardingWizard';
import MagicLogin from './pages/MagicLogin';
import OrbVoiceIntake from './pages/OrbVoiceIntake';
import PinSetup from './pages/PinSetup';
import RideChatPage from './pages/RideChatPage';
import MatchInbox from './pages/MatchInbox';
import ProfileView from './pages/ProfileView';
import EmergencyContacts from './pages/EmergencyContacts';
import EventDirectory from './pages/EventDirectory';
import OrbAgent from './pages/OrbAgent';
import CommuteInvite from './pages/CommuteInvite';
import CreateCommute from './pages/CreateCommute';
import SosButton from './components/SosButton';
import { TranslationProvider } from './contexts/TranslationContext';
import { GlobalTranslateButton } from './components/GlobalTranslateButton';
import React from 'react';

// ============================================================================
// ERROR BOUNDARY — prevents one route's crash from taking down the whole app
// ============================================================================
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Caught:', error, info?.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <h2 style={{ color: '#ef4444', marginBottom: '12px' }}>Something went wrong</h2>
          <p style={{ color: '#64748b', marginBottom: '20px' }}>{this.state.error?.message}</p>
          <button onClick={() => this.setState({ hasError: false, error: null })}
            style={{ padding: '8px 24px', borderRadius: '8px', background: '#6366f1', color: '#fff', border: 'none', cursor: 'pointer' }}>
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============================================================================
// ADMIN ROUTE COMPONENT
// ============================================================================
const AdminRoute = ({ children }) => {
  const { user, isAuthenticated, loading, token } = useAuth();

  // Defensive: while we have a token but the user object hasn't materialised
  // yet (e.g. mid-mount or background re-fetch), keep showing the spinner.
  // This kills the race-condition where a hard reload on /admin bounced to
  // /login because the AdminRoute evaluated before /auth/me had returned.
  if (loading || (token && !user)) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!user?.is_admin) return <Navigate to="/" />;
  return children;
};

// ============================================================================
// APP CONTENT (with Navbar & Notifications)
// ============================================================================
const AppContent = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  // Native FCM push registration (Capacitor APK only — silent no-op on web).
  // Detects which APK we're running in via the URL path so the right Firebase
  // app_id is registered server-side.
  useFcmPush({
    enabled: isAuthenticated,
    appId: window.location.pathname.startsWith('/orb')
      ? 'com.voiceride.guardian.orb'
      : 'com.voiceride.app',
    onNavigate: (url) => {
      if (url && typeof url === 'string') window.location.assign(url);
    },
  });

  useEffect(() => {
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language, isRtl]);

  // CRITICAL: Detect session_id in URL hash from Google OAuth redirect
  if (window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const isSafetyDashboard = window.location.pathname === '/safety-dashboard';
  const isOrbPage = window.location.pathname === '/orb';
  const isOrbApp = window.location.pathname === '/orb-app';
  const isFullscreen = isSafetyDashboard || isOrbPage || isOrbApp;

  return (
    <div className={`app ${isRtl ? 'rtl' : 'ltr'}`} style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Global fixed background + overlay */}
      <div className="app-bg" />
      <div className="app-bg-overlay" />

      {/* Dynamic Header with logo + app name — hide on standalone dashboards */}
      {!isFullscreen && <Header />}

      {isAuthenticated && !isFullscreen && <Navbar user={user} onLogout={logout} />}
      {isAuthenticated && !isFullscreen && (
        <div className="notifications-wrapper">
          <NotificationsPanel />
        </div>
      )}

      {isAuthenticated && !isFullscreen && <SosButton />}

      <div style={{ flex: 1 }}>
      <RouteErrorBoundary>
      <Routes>
        {/* ════════════════ PUBLIC ROUTES (no auth) ════════════════ */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/magic" element={<MagicLogin />} />
        <Route path="/pin-setup" element={<PinSetup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/impressum" element={<Impressum />} />
        <Route path="/agb" element={<AGB />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/preise-b2b" element={<PublicPricingPage />} />
        <Route path="/pricing-b2b" element={<PublicPricingPage />} />

        {/* ════════════════ ORB APP (separate experience) ════════════════ */}
        {/* Orb has its own login flow inline (OrbLogin in OrbAppPage). */}
        {/* Back-navigation stays inside the Orb domain — never bounces into the dashboard. */}
        <Route path="/orb" element={<OrbPage />} />
        <Route path="/orb-app" element={<OrbAppPage />} />
        <Route path="/orb/voice-register" element={<OrbVoiceIntake />} />
        <Route path="/safety-dashboard" element={<SafetyDashboard />} />

        {/* ════════════════ MITFAHRZENTRALE (auth required) ════════════════ */}
        <Route path="/" element={
          <ProtectedRoute>
            {/* Chameleon: B2B user → Einsatzplan, Standard → VoiceMatch */}
            <ChameleonHome />
          </ProtectedRoute>
        } />
        <Route path="/b2b-daily" element={<ProtectedRoute><B2BDailyRoute /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/profile-setup" element={<ProfileSetup />} />
        <Route path="/profile/:userId" element={<ProtectedRoute><ProfileView /></ProtectedRoute>} />
        <Route path="/emergency-contacts" element={<ProtectedRoute><EmergencyContacts /></ProtectedRoute>} />
        <Route path="/events" element={<EventDirectory />} />
        <Route path="/orb-chat" element={<ProtectedRoute><OrbAgent /></ProtectedRoute>} />
        <Route path="/commute/:token" element={<CommuteInvite />} />
        <Route path="/create-commute" element={<ProtectedRoute><CreateCommute /></ProtectedRoute>} />
        <Route path="/onboarding" element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />
        <Route path="/test-map" element={<ProtectedRoute><TestMapPage /></ProtectedRoute>} />
        <Route path="/create-ride" element={<ProtectedRoute><CreateRide /></ProtectedRoute>} />
        <Route path="/search" element={<RouteErrorBoundary><RideSearch /></RouteErrorBoundary>} />
        <Route path="/booking-success" element={<BookingSuccess />} />
        <Route path="/my-rides" element={<ProtectedRoute><MyRides /></ProtectedRoute>} />
        <Route path="/driver-online" element={<ProtectedRoute><DriverOnline /></ProtectedRoute>} />
        <Route path="/driver/earnings" element={<ProtectedRoute><DriverEarnings /></ProtectedRoute>} />
        <Route path="/phone-verification" element={<ProtectedRoute><PhoneVerification /></ProtectedRoute>} />
        <Route path="/chat/:rideId" element={<ProtectedRoute><RideChatPage /></ProtectedRoute>} />
        <Route path="/matches" element={<ProtectedRoute><MatchInbox /></ProtectedRoute>} />
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />

        {/* Fallback → home (which itself is auth-gated) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </RouteErrorBoundary>
      </div>

      {!isFullscreen && <Footer />}
    </div>
  );
};

// ============================================================================
// MAIN APP COMPONENT
// ============================================================================
function ChameleonHome() {
  const { user } = useAuth();
  // B2B user with org_id -> Show Einsatzplan
  if (user?.org_id) return <B2BDailyRoute />;
  // Standard user -> VoiceMatch (Mitfahrzentrale)
  return <VoiceMatch />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <TranslationProvider>
          <Routes>
            <Route path="/*" element={<AppContent />} />
          </Routes>
          <GlobalTranslateButton />
        </TranslationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
